import { StoreWithSelectListData } from "src/shared/models/store-list-with-select-data";
import { Store2 } from "stores/store-2";
import { TeamOrAgentType } from "src/shared/models/models";
import {
  GOnAgentModifiedSubscription,
  GOnAgentModifiedSubscriptionVariables,
  OnAgentModifiedDocument,
  GOnAgentCreatedSubscription,
  GOnAgentCreatedSubscriptionVariables,
  OnAgentCreatedDocument,
  OnTeamCreatedDocument,
  GOnTeamCreatedSubscription,
  GOnTeamCreatedSubscriptionVariables,
  GOnTeamModifiedSubscription,
  GOnTeamModifiedSubscriptionVariables,
  OnTeamModifiedDocument,
  GAgentStatusItem,
} from "src/generated/graphql";
import { teamListGlobalStore } from "src/stores/team-list-global-store";

export interface ConversationAssignItem {
  id: string;
  label: string;
  type: TeamOrAgentType;
  ui: {
    index: number;
    selected: boolean;
    hover: boolean;
    key: number;
  };
}

export type ConversationAssign = ConversationAssignItem & StoreWithSelectListData;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ConversationAssignData {}

export class ConversationAssignGlobalStore extends Store2<ConversationAssignData, ConversationAssign> {
  protected data(): ConversationAssignData {
    return {};
  }

  protected setupSubscribe() {
    this.addSubscription<GOnAgentModifiedSubscription, GOnAgentModifiedSubscriptionVariables>(
      OnAgentModifiedDocument,
      {},
      res => {
        if (res.onAgentModified) {
          if (res.onAgentModified.perTenant.status !== GAgentStatusItem.Active) {
            this.removeByFilter(i => i.id === res.onAgentModified.id);
          } else {
            this.replaceById(res.onAgentModified.id, {
              id: res.onAgentModified.id,
              label: res.onAgentModified.name,
              type: TeamOrAgentType.Agent,
            });
          }
        }
      },
    );

    this.addSubscription<GOnAgentCreatedSubscription, GOnAgentCreatedSubscriptionVariables>(
      OnAgentCreatedDocument,
      {},
      res => {
        if (res.onAgentCreated) {
          this.push({
            id: res.onAgentCreated.id,
            label: res.onAgentCreated.name,
            type: TeamOrAgentType.Agent,
          });
        }
      },
    );

    this.addSubscription<GOnTeamModifiedSubscription, GOnTeamModifiedSubscriptionVariables>(
      OnTeamModifiedDocument,
      {},
      res => {
        if (res.onTeamModified) {
          if (res.onTeamModified.deleted) {
            this.removeByFilter(i => i.id === res.onTeamModified.id);
          } else {
            this.replaceById(res.onTeamModified.id, {
              id: res.onTeamModified.id,
              label: res.onTeamModified.name,
              type: TeamOrAgentType.Team,
            });
          }
        }
      },
    );

    this.addSubscription<GOnTeamCreatedSubscription, GOnTeamCreatedSubscriptionVariables>(
      OnTeamCreatedDocument,
      {},
      res => {
        if (res.onTeamCreated) {
          this.push({
            id: res.onTeamCreated.id,
            label: res.onTeamCreated.name,
            type: TeamOrAgentType.Team,
          });
        }
      },
    );
  }

  private flatAssignList: ConversationAssignItem[] = [];
  private structuredTeamList: {
    team: ConversationAssign;
    agents: ConversationAssign[];
  }[] = [];

  getAllAgents(): ConversationAssign[] {
    return super.getList().filter(i => i.type === TeamOrAgentType.Agent);
  }

  updateFromTeamList() {
    const teamData = teamListGlobalStore.getList();
    const allAgents = this.getAllAgents();
    const flatList: ConversationAssign[] = [];
    const structured: {
      team: ConversationAssignItem;
      agents: ConversationAssignItem[];
    }[] = [];

    const assignedAgentIds = new Set<string>();

    for (const [index, team] of teamData.entries()) {
      const teamItem: ConversationAssign = {
        id: team.id,
        label: team.name,
        type: TeamOrAgentType.Team,
        ui: {
          index,
          selected: false,
          hover: false,
          key: index,
        },
      };

      const agents: ConversationAssign[] = team.agents.map((agent, i) => {
        assignedAgentIds.add(agent.id);
        return {
          id: agent.id,
          label: agent.name,
          type: TeamOrAgentType.Agent,
          ui: {
            index: i,
            selected: false,
            hover: false,
            key: i,
          },
        };
      });

      flatList.push(teamItem, ...agents);
      structured.push({ team: teamItem, agents });
    }

    // Agents not in teams
    const unassignedAgents = allAgents
      .filter(agent => !assignedAgentIds.has(agent.id))
      .map((agent, i) => ({
        ...agent,
        ui: {
          index: flatList.length + i,
          selected: false,
          hover: false,
          key: flatList.length + i,
        },
      }));

    flatList.push(...unassignedAgents);

    this.flatAssignList = flatList;
    this.structuredTeamList = structured;
  }


  getList(): readonly ConversationAssign[] {
    return this.flatAssignList;
  }

  getTeamsWithAgents(): { team: ConversationAssignItem; agents: ConversationAssignItem[] }[] {
    return this.structuredTeamList;
  }
}


export const conversationAssignStore = new ConversationAssignGlobalStore();
